import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, Lock, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Direct API base — this page is PUBLIC and must NOT use the JWT-auth axios instance
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DeptResolve() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: "", data: null, done: false });
  const [reasonId, setReasonId] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios
      .get(`${API_BASE}/dept/resolve/${token}`)
      .then((r) => setState({ loading: false, error: "", data: r.data, done: false }))
      .catch((e) =>
        setState({ loading: false, error: e?.response?.data?.detail || "Invalid or expired link.", data: null, done: false })
      );
  }, [token]);

  const submit = async () => {
    if (!reasonId) return toast.error("Pick a closure reason");
    setSubmitting(true);
    try {
      const reason = state.data.closure_reasons.find((r) => r.id === reasonId);
      await axios.post(`${API_BASE}/dept/resolve/${token}`, {
        closure_reason_id: reasonId,
        closure_reason_name: reason?.name || "",
        notes,
      });
      setState((s) => ({ ...s, done: true }));
      toast.success("Ticket resolved — thank you!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (state.error) {
    return (
      <ResolveShell>
        <div className="text-center py-8">
          <AlertTriangle className="w-10 h-10 mx-auto text-rose-400 mb-3" />
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-2">Link unavailable</h2>
          <p className="text-sm text-slate-500">{state.error}</p>
        </div>
      </ResolveShell>
    );
  }

  if (state.done) {
    return (
      <ResolveShell>
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-50 border border-emerald-200 grid place-items-center mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-2">Resolved</h2>
          <p className="text-sm text-slate-500">
            Ticket <span className="font-mono">{state.data.ticket.code}</span> has been marked resolved.
            The support team has been notified.
          </p>
        </div>
      </ResolveShell>
    );
  }

  const t = state.data.ticket;
  const expired = state.data.already_resolved;

  return (
    <ResolveShell>
      <div className="space-y-5" data-testid="dept-resolve-page">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">
              <ShieldCheck className="w-3 h-3 mr-1" /> Secure department link
            </Badge>
            {expired && <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-600 border-slate-200">Already {t.status}</Badge>}
          </div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">{t.subject}</h1>
          <div className="text-xs text-slate-500 mt-1 font-mono">{t.code} · {t.department_name || "—"} · {t.priority || "—"}</div>
        </div>

        <Card className="border-slate-200 bg-slate-50/40">
          <CardContent className="p-4 space-y-3 text-sm">
            <Row label="Customer" value={t.customer_name} />
            <Row label="Category" value={`${t.category || "—"}${t.subcategory ? ` → ${t.subcategory}` : ""}`} />
            <Row label="Concern" value={t.description} multiline />
          </CardContent>
        </Card>

        {!expired && (
          <Card className="border-slate-200">
            <CardContent className="p-5 space-y-4">
              <div>
                <Label className="text-xs font-semibold text-slate-700">Closure reason *</Label>
                <Select value={reasonId} onValueChange={setReasonId}>
                  <SelectTrigger className="mt-1.5" data-testid="dept-closure-select">
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {state.data.closure_reasons.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Resolution notes (optional)</Label>
                <Textarea
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="What was done? Customer feedback? Reference numbers?"
                  className="mt-1.5 resize-none"
                  data-testid="dept-notes-input"
                />
              </div>
              <div className="flex items-center justify-between pt-2">
                <div className="text-[11px] text-slate-400 flex items-center gap-1.5">
                  <Lock className="w-3 h-3" /> Link valid for 7 days; one-time signed by Kippu.
                </div>
                <Button
                  onClick={submit}
                  disabled={submitting || !reasonId}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="dept-resolve-submit"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                  {submitting ? "Submitting..." : "Mark resolved"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </ResolveShell>
  );
}

function ResolveShell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-center mb-6">
          <div className="inline-flex items-end gap-3">
            <img src="/brand/kippu-logo.svg" alt="Kippu" className="h-11 w-auto rounded-md" />
            <div className="flex flex-col leading-none pb-1.5">
              <span className="text-[10px] uppercase tracking-[0.24em] text-slate-500">BY</span>
              <span className="text-xs uppercase tracking-[0.24em] text-slate-700 font-medium mt-1">ZEFFICACY</span>
            </div>
          </div>
        </div>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-7">{children}</CardContent>
        </Card>
        <p className="text-[11px] text-center text-slate-400 mt-6">© 2026 Zefficacy · Kippu Omnichannel</p>
      </div>
    </div>
  );
}

function Row({ label, value, multiline }) {
  return (
    <div className={multiline ? "" : "flex items-start gap-3"}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold w-24 shrink-0 mt-0.5">{label}</div>
      <div className={`text-slate-800 ${multiline ? "mt-1 whitespace-pre-wrap" : "flex-1"}`}>{value || "—"}</div>
    </div>
  );
}
